import { FC, useContext, useMemo, useState, useEffect } from 'react';
import {
	Box,
	Image,
	Text,
	Button,
	Flex,
	HStack,
	Avatar,
} from 'src/components/ui';
import {
	ICreative,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import MiniGlobeIcon from 'src/assets/icons/MiniGlobe';
import { IChannelCreativeAttributes } from 'src/lib/schemas';

import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview from 'src/components/templates/TemplatePreview';
import DDPhotoEditor, {
	DDPhotoEditorProps,
} from '../../design-directions/DDPhotoEditor';
import CreativeCard from '../CreativeCard';
import { formatCallToAction } from '../utils/formatedCTA';
import UserContext from 'src/contexts/user/UserContext';

interface SquareCreativeProps extends Partial<DDPhotoEditorProps> {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	editMode?: boolean;
	isPaid?: boolean;
	selectedHeadline?: string;
	selectedDescription?: string;
}

const SquareCreative: FC<SquareCreativeProps> = ({
	creative,
	withActions = true,
	size = 'md',
	creativesConfig,
	editMode = false,
	isPaid,
	selectedHeadline,
	selectedDescription,
	...photoEditorProps
}) => {
	const { account } = useContext(UserContext);
	const { callToAction, headlines, descriptions } = (creative as any)
		.attributes;
	const headline = selectedHeadline ?? headlines?.[0];
	const description = selectedDescription ?? descriptions?.[0];
	const CTA = callToAction ?? 'Contact us';
	const [logo, setLogo] = useState<string | undefined>(account?.logoSquare);

	useEffect(() => {
		setLogo(account?.logoSquare);
	}, [account]);

	const isPending =
		creative.status === 'pending' || creative.status === 'generating';

	const isCreatomate =
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes);

	const normalizedTemplate = useMemo(() => {
		if (isCreatomate) {
			const clone = JSON.parse(JSON.stringify(creative.attributes));
			if (Array.isArray(clone.elements)) {
				clone.elements = clone.elements.map((el: any) => {
					if (typeof el.x === 'string') el.x = el.x.trim();
					if (typeof el.y === 'string') el.y = el.y.trim();
					if (typeof el.width === 'string') el.width = el.width.trim();
					if (typeof el.height === 'string') el.height = el.height.trim();
					if (el.elements && Array.isArray(el.elements)) {
						el.elements = el.elements.map((subEl: any) => {
							if (typeof subEl.x === 'string') subEl.x = subEl.x.trim();
							if (typeof subEl.y === 'string') subEl.y = subEl.y.trim();
							if (typeof subEl.width === 'string')
								subEl.width = subEl.width.trim();
							if (typeof subEl.height === 'string')
								subEl.height = subEl.height.trim();
							return subEl;
						});
					}
					return el;
				});
			}
			return clone;
		}
		return null;
	}, [JSON.stringify(creative.attributes)]);

	return (
		<CreativeCard
			creative={creative}
			withActions={withActions}
			size={size}
			title="Square"
			creativesConfig={creativesConfig}
		>
			{isPending ? (
				<Flex width="100%" minH={{ base: '400px', md: '677px' }} justifyContent="center">
					<FusionLoading isLoading />
				</Flex>
			) : (
				<>
					<Box p={4}>
						<Flex alignItems="center" gap={2}>
							<Avatar
								key={logo || 'no-logo'}
								src={logo}
								sx={{
									img: { objectFit: 'contain' },
								}}
							/>
							<Box>
								<Text fontWeight="bold">{account?.name}</Text>
								<HStack spacing={1}>
									{isPaid && (
										<>
											<Text color="black" fontSize="sm">
												Sponsored ·
											</Text>
											<MiniGlobeIcon />
										</>
									)}
								</HStack>
							</Box>
						</Flex>
					</Box>
					<Box h={{ base: '250px', md: '382px' }} p={4}>
						{isCreatomate && normalizedTemplate ? (
							<TemplatePreview
								key={JSON.stringify((creative as any).attributes.elements)}
								template={normalizedTemplate}
								borderRadius="5px"
							/>
						) : !editMode ? (
							<Image
								key={(creative as any).attributes.image?.flatFile}
								src={(creative as any).attributes.image?.flatFile}
								alt="Preview"
								fallbackSrc="/fusion-loader.gif"
								borderRadius="5px"
							/>
						) : (
							photoEditorProps.designDirection &&
							photoEditorProps.isChangingLayer !== undefined &&
							photoEditorProps.onRefetchCreatives && (
								<DDPhotoEditor
									key={photoEditorProps.layeredFile}
									layeredFile={photoEditorProps.layeredFile}
									textLayersContent={photoEditorProps.textLayersContent}
									mediaLayersContent={photoEditorProps.mediaLayersContent}
									fonts={photoEditorProps.fonts}
									designDirection={photoEditorProps.designDirection}
									isChangingLayer={photoEditorProps.isChangingLayer}
									onRefetchCreatives={photoEditorProps.onRefetchCreatives}
									withActions={false}
									containerHeight="382px"
								/>
							)
						)}
					</Box>
					<Box px={6} borderBottomRadius="lg" mb={4}>
						<Text
							data-testid="creative-headline"
							fontWeight="bold"
							noOfLines={1}
							size="md"
							mb={2}
						>
							{headline}
						</Text>
						<Text fontSize="sm" mb={4}>
							{description}
						</Text>
						<Button
							variant="orangeSolid"
							borderRadius="3xl"
							w="full"
							pointerEvents="none"
						>
							{formatCallToAction(CTA)}
						</Button>
					</Box>
				</>
			)}
		</CreativeCard>
	);
};

export default SquareCreative;
